'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ChevronRight, Flag, Calendar, MapPin, ChevronLeft } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';


interface NextRaceEvent {
  id: string;
  name: string;
  date: string;
  venue: string;
  city: string;
  organizer_name?: string;
  organizer_id?: string;
}

function formatCountdown(targetDate: string) {
  const now = new Date();
  const target = new Date(targetDate);
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(diff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
  const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
  const seconds = Math.floor(diff % (1000 * 60) / 1000);
  return { days, hours, minutes, seconds };
}

function formatDisplayDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function HeroSection() {
  const { user } = useAuth();
  const supabase = createClient();
  const [countdowns, setCountdowns] = useState<{ days: number; hours: number; minutes: number; seconds: number }[]>([]);
  const [mounted, setMounted] = useState(false);
  const [nearestEvents, setNearestEvents] = useState<NextRaceEvent[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const touchStartX = useRef<number>(0);

  useEffect(() => {
    setMounted(true);
    const fetchNearestEvents = async () => {
      const today = new Date().toISOString().split('T')[0];
      // Get all upcoming events, find nearest date, then get all events on that date
      const { data } = await supabase
        .from('race_events')
        .select('id, name, date, venue, city, organizer_name, organizer_id')
        .in('status', ['upcoming', 'live'])
        .gte('date', today)
        .order('date', { ascending: true })
        .limit(10);

      if (data && data.length > 0) {
        const nearestDate = data[0].date;
        // Get all events on the nearest date
        const sameDay = data.filter((e: any) => e.date === nearestDate);
        setNearestEvents(sameDay as NextRaceEvent[]);
      } else {
        // Fallback
        const { data: fallback } = await supabase
          .from('race_events')
          .select('id, name, date, venue, city, organizer_name, organizer_id')
          .in('status', ['upcoming', 'live'])
          .order('date', { ascending: true })
          .limit(3);
        if (fallback && fallback.length > 0) {
          const nearestDate = fallback[0].date;
          setNearestEvents(fallback.filter((e: any) => e.date === nearestDate) as NextRaceEvent[]);
        }
      }
    };
    fetchNearestEvents();
  }, []);

  useEffect(() => {
    if (nearestEvents.length === 0) return;
    const update = () => {
      setCountdowns(nearestEvents.map(e => formatCountdown(e.date)));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [nearestEvents]);

  const activeEvent = nearestEvents[activeIdx];
  const activeCountdown = countdowns[activeIdx] || { days: 0, hours: 0, minutes: 0, seconds: 0 };

  const countdownItems = [
    { label: 'Days', value: activeCountdown.days },
    { label: 'Hours', value: activeCountdown.hours },
    { label: 'Mins', value: activeCountdown.minutes },
    { label: 'Secs', value: activeCountdown.seconds },
  ];

  const handlePrev = () => setActiveIdx(p => (p - 1 + nearestEvents.length) % nearestEvents.length);
  const handleNext = () => setActiveIdx(p => (p + 1) % nearestEvents.length);

  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) handleNext();
      else handlePrev();
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden hero-gradient">
      <div className="absolute inset-0 checkered-bg opacity-30 pointer-events-none" />
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary via-accent to-primary opacity-80" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full bg-primary/8 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full bg-accent/6 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-20 md:py-24 w-full">
        <div className="mb-10">
          <div className="inline-block" />
        </div>

        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary live-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              2026 Season — {activeEvent ? `${activeEvent.name} Approaching` : 'Season Underway'}
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl xl:text-8xl font-extrabold uppercase leading-none tracking-tight mb-4">
            <span className="text-foreground">SPEED</span>
            <br />
            <span className="text-[rgba(232,17,17,1)]">PASS</span>
          </h1>

          <p className="text-base md:text-xl text-muted-foreground font-medium max-w-xl mb-8 leading-relaxed">One Stop Racing Centre.</p>

          <div className="flex flex-wrap gap-3 mb-12">
            {!user &&
              <Link href="/register" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary">
                Register Now <ChevronRight size={16} />
              </Link>
            }
            <Link href="/events" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-secondary">
              View Events <Calendar size={16} />
            </Link>
            <Link href="/live-results" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wide rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150">
              <span className="w-2 h-2 rounded-full bg-primary live-pulse" />
              Live Results
            </Link>
          </div>

          {/* Next Race Countdown — slideable if multiple events */}
          {activeEvent && (
            <div
              className="bg-card/80 border border-border rounded-xl p-4 md:p-5 inline-block backdrop-blur-sm w-full max-w-lg"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {nearestEvents.length > 1 ? `Next Race (${activeIdx + 1}/${nearestEvents.length})` : 'Next Race'}
                </p>
                {nearestEvents.length > 1 && (
                  <div className="flex items-center gap-1">
                    <button onClick={handlePrev} className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors">
                      <ChevronLeft size={14} className="text-foreground" />
                    </button>
                    <button onClick={handleNext} className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors">
                      <ChevronRight size={14} className="text-foreground" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link href={`/events?event=${activeEvent.id}`} className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">
                  <Flag size={15} className="text-primary shrink-0" />
                  {activeEvent.name}
                </Link>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar size={14} className="text-primary shrink-0" />
                  {formatDisplayDate(activeEvent.date)}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin size={14} className="text-primary shrink-0" />
                  {activeEvent.venue}{activeEvent.city ? `, ${activeEvent.city}` : ''}
                </div>
              </div>

              {/* Dots indicator */}
              {nearestEvents.length > 1 && (
                <div className="flex items-center gap-1.5 mb-4">
                  {nearestEvents.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${i === activeIdx ? 'w-5 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
                    />
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3">
                {countdownItems.map((item, i) =>
                  <React.Fragment key={`countdown-${item.label}`}>
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl font-extrabold font-tabular text-foreground leading-none">
                        {mounted ? String(item.value).padStart(2, '0') : '00'}
                      </div>
                      <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mt-1">
                        {item.label}
                      </div>
                    </div>
                    {i < countdownItems.length - 1 &&
                      <span className="text-primary font-bold text-xl mb-3">:</span>
                    }
                  </React.Fragment>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}
